<template>
	<div>
		<!-- Show list view when filter is active -->
		<div v-if="isFilterActive" class="account-list">
			<!-- Current Accounts -->
			<template v-if="showCurrentCard && currentAccounts.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Current accounts" :rightLabel="getCurrentAccountsBalance()" class="section-header" />
				<AccountListItem
					v-for="account in currentAccounts"
					:key="`current-${account.id}`"
					:provider-name="account.bank?.name || 'Bank'"
					:line1="account.accountType?.prettyName || 'Current'"
					:line2="account.accountNumber || ''"
					:value="this.formatter.format(account.balance)"
					label="Balance"
					:avatar-url="account.bank?.logoUri || getBrandLogo(account.bank?.name)"
					:badge-url="account.source === 0 ? require('@/assets/openbankingmarker.png') : null"
					@click="handleAccountListItemClick(account, 'current')"
				/>
			</template>

			<!-- Credit Accounts -->
			<template v-if="showCreditCard && creditAccounts.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Credit Cards" :rightLabel="getCreditCardsBalance()" class="section-header" />
				<AccountListItem
					v-for="account in creditAccounts"
					:key="`credit-${account.id}`"
					:provider-name="account.bank?.name || 'Bank'"
					:line1="account.accountType?.prettyName || 'Credit'"
					:line2="account.accountNumber || ''"
					:value="this.formatter.format(account.balance)"
					label="Balance"
					:avatar-url="account.bank?.logoUri || getBrandLogo(account.bank?.name)"
					:badge-url="account.source === 0 ? require('@/assets/openbankingmarker.png') : null"
					@click="handleAccountListItemClick(account, 'credit')"
				/>
			</template>

			<!-- Savings Accounts -->
			<template v-if="showSavingCard && savingAccounts.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Savings accounts" :rightLabel="getSavingAccountsBalance()" class="section-header" />
				<AccountListItem
					v-for="account in savingAccounts"
					:key="`savings-${account.id}`"
					:provider-name="account.bank?.name || 'Bank'"
					:line1="account.accountType?.prettyName || 'Savings'"
					:line2="account.accountNumber || ''"
					:value="this.formatter.format(account.balance)"
					label="Balance"
					:avatar-url="account.bank?.logoUri || getBrandLogo(account.bank?.name)"
					:badge-url="account.source === 0 ? require('@/assets/openbankingmarker.png') : null"
					@click="handleAccountListItemClick(account, 'savings')"
				/>
			</template>

			<!-- Mortgage Accounts -->
			<template v-if="showMortgageCard && mortgageInfo && mortgageInfo.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Mortgages" class="section-header" />
				<AccountListItem
					v-for="account in mortgageInfo"
					:key="`mortgage-${account.id}`"
					:provider-name="account.account?.provider?.name || 'Provider'"
					:line1="account.policyType?.name || 'Mortgage'"
					:line2="account.account?.reference || ''"
					:value="this.formatter.format(account.account?.balance?.amount || 0)"
					label="Balance"
					:avatar-url="getBrandLogo(account.account?.provider?.name)"
					@click="handleAccountListItemClick(account, 'mortgage')"
				/>
			</template>

			<!-- Loans (Credit Info) -->
			<template v-if="showCreditInfo && creditInfo && creditInfo.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Loans" class="section-header" />
				<AccountListItem
					v-for="account in creditInfo"
					:key="`loan-${account.id}`"
					:provider-name="account.provider?.name || 'Provider'"
					:line1="account.creditType?.name || 'Loan'"
					:line2="account.reference || ''"
					:value="this.formatter.format(account.balance?.amount || 0)"
					label="Balance"
					:avatar-url="getBrandLogo(account.provider?.name)"
					@click="handleAccountListItemClick(account, 'loan')"
				/>
			</template>

			<!-- Pensions (investmentInfo filtered by accountType.name === 'Pension') -->
			<template v-if="showPensionCard && pensionAccounts && pensionAccounts.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Pensions" class="section-header" />
				<AccountListItem
					v-for="account in pensionAccounts"
						:key="`pension-${account.id}`"
						:provider-name="account.provider?.name || 'Provider'"
						:line1="account.investmentType?.name || 'Pension'"
						:line2="account.reference || ''"
						:value="typeof account.balance === 'number' ? this.formatter.format(account.balance) : this.formatter.format(0)"
						label="Balance"
						:avatar-url="getBrandLogo(account.provider?.name)"
						@click="handleAccountListItemClick(account, 'pension')"
					/>
			</template>

			<!-- Investments (investmentInfo filtered by accountType.name === 'Investment') -->
			<template v-if="showInvestmentCard && investmentAccounts && investmentAccounts.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Investments" class="section-header" />
				<AccountListItem
					v-for="account in investmentAccounts"
					:key="`investment-${account.id}`"
					:provider-name="account.provider?.name || 'Provider'"
					:line1="account.investmentType?.name || 'Investment'"
					:line2="account.reference || ''"
					:value="typeof account.balance === 'number' ? this.formatter.format(account.balance) : this.formatter.format(0)"
					label="Balance"
					:avatar-url="getBrandLogo(account.provider?.name)"
					@click="handleAccountListItemClick(account, 'investment')"
				/>
			</template>


			<!-- Insurance Accounts -->
			<template v-if="showInsuranceCard && insuranceInfo && insuranceInfo.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Insurance Policies" class="section-header" />
				<AccountListItem
					v-for="account in insuranceInfo"
					:key="`insurance-${account.id}`"
					:provider-name="account.provider?.name || 'Provider'"
					:line1="account.policyType?.name || 'Insurance'"
					:line2="account.policyNumber || ''"
					:value="account.endDateString"
					label="Renewal"
					:avatar-url="getBrandLogo(account.provider?.name)"
					@click="handleAccountListItemClick(account, 'policy')"
				/>
			</template>


			<!-- Suggested Insurance Assets -->
			<transition-group name="fade" v-if="(selectedSection === 'all' || selectedSection === 'ins')">
				<AccountListItem
					v-for="(asset, index) in suggestedInsuranceAssets"
					:key="`suggested-ins-${index}`"
					:provider-name="properCase(asset.uiElement.title)"
					:line1="asset.uiElement.subTitle"
					line2=""
					:label="asset.uiElement.rightTopValue ? asset.uiElement.rightTopLabel : ''"
					:value="_processMonetaryAmounts(asset.uiElement.rightTopValue || '')"
					:label2="asset.uiElement.rightBottomValue ? asset.uiElement.rightBottomLabel : ''"
					:value2="_processMonetaryAmounts(asset.uiElement.rightBottomValue || '')"
					:avatar-url="getBrandLogo(asset.provider)"
					:badge-url="require('@/assets/aisummary.png')"
					@click="$emit('suggested-asset-click', asset)"
				/>
			</transition-group>

			<!-- Suggested Long-term Assets -->
			<transition-group name="fade" v-if="(selectedSection === 'all' || selectedSection === 'lt')">
				<AccountListItem
					v-for="(asset, index) in suggestedLongTermAssets"
					:key="`suggested-lt-${index}`"
					:provider-name="properCase(asset.uiElement.title)"
					:line1="asset.uiElement.subTitle"
					line2=""
					:label="asset.uiElement.rightTopValue ? asset.uiElement.rightTopLabel : ''"
					:value="_processMonetaryAmounts(asset.uiElement.rightTopValue || '')"
					:label2="asset.uiElement.rightBottomValue ? asset.uiElement.rightBottomLabel : ''"
					:value2="_processMonetaryAmounts(asset.uiElement.rightBottomValue || '')"
					:avatar-url="getBrandLogo(asset.provider)"
					:badge-url="require('@/assets/aisummary.png')"
					@click="$emit('suggested-asset-click', asset)"
				/>
			</transition-group>

			<!-- Suggested Day-to-day Assets -->
			<transition-group name="fade" v-if="(selectedSection === 'all' || selectedSection === 'd2d')">
				<AccountListItem
					v-for="(asset, index) in suggestedDayToDayAssets"
					:key="`suggested-d2d-${index}`"
					:provider-name="properCase(asset.uiElement.title)"
					:line1="asset.uiElement.subTitle"
					line2=""
					:label="asset.uiElement.rightTopValue ? asset.uiElement.rightTopLabel : ''"
					:value="_processMonetaryAmounts(asset.uiElement.rightTopValue || '')"
					:label2="asset.uiElement.rightBottomValue ? asset.uiElement.rightBottomLabel : ''"
					:value2="_processMonetaryAmounts(asset.uiElement.rightBottomValue || '')"
					:avatar-url="getBrandLogo(asset.provider)"
					:badge-url="require('@/assets/aisummary.png')"
					@click="$emit('suggested-asset-click', asset)"
				/>
			</transition-group>

			<!-- Empty State -->
			<div v-if="showEmptyState" class="empty-state">
				<p class="empty-state-text">{{ emptyStateHeadline }}</p>
				<p class="empty-state-subtext">{{ emptyStateSubtext }}</p>
				<MGButton
					:fullWidth="true"
					:noCaps="true"
					:stacked="true"
					:closerStack="true"
					:text="emptyStateButtonText"
					:plus="true"
					@click="handleAddAccountClick"
				/>
			</div>

			<MGButton
			v-if="selectedSection === 'd2d' && currentAccounts.length > 0 && !showEmptyState"
				:fullWidth="true"
				:noCaps="true"
				text="See spending analysis"
				:outline="true"
				:arrow="true"
				@click="handleSpendingAnalysisClick"
				:icon="require('@/assets/spending-analysis.png')"
				leftIconSize="16"
			/>
			<MGButton
			v-if="(selectedSection === 'd2d' || selectedSection === 'lt' || selectedSection === 'ins') && !showEmptyState"
				class="add-account-button"
				:fullWidth="true"
				:noCaps="true"
				:stacked="true"
				:closerStack="true"
				:text="emptyStateButtonText"
				:plus="true"
				@click="handleAddAccountClick"
			/>

		</div>

		<!-- Show card view when filter is not active -->
		<div v-else>
			<!-- Single grid when 5 or fewer cards -->
			<div v-if="!showSectionHeaders" class="account-cards-grid">
				<AccountCard
					v-if="currentAccounts.length > 0 && showCurrentCard"
					key="current-account"
					label="Current"
					:icon="getCurrentAccountIcon()"
					:accountIcons="getAllCurrentAccountIcons()"
					:balance="formatter.format(currentAccountsBalance)"
					@click="handleCurrentAccountClick"
				/>

				<AccountCard
					v-if="creditAccounts.length > 0 && showCreditCard"
					key="credit-account"
					label="Credit Card"
					:icon="getCreditCardIcon()"
					:accountIcons="getAllCreditCardIcons()"
					:balance="formatter.format(creditAccountsBalance)"
					@click="handleCreditAccountClick"
				/>

				<AccountCard
					v-if="savingAccounts.length > 0 && showSavingCard"
					key="savings-account"
					label="Savings"
					:icon="getSavingsAccountIcon()"
					:accountIcons="getAllSavingsAccountIcons()"
					:balance="formatter.format(savingAccountsBalance)"
					@click="handleSavingAccountClick"
				/>

				<AccountCard
					v-if="mortgageInfo && mortgageInfo.length > 0 && showMortgageCard"
					key="mortgage"
					label="Mortgage"
					:icon="getMortgageIcon()"
					:accountIcons="getAllMortgageAccountIcons()"
					:balance="formatter.format(mortgageBalance)"
					@click="handleMortgageClick"
				/>

				<AccountCard
					v-if="insuranceInfo && insuranceInfo.length > 0 && showInsuranceCard"
					key="insurance"
					label="Insurance Policies"
					:icon="getInsuranceIcon()"
					:accountIcons="getAllInsuranceAccountIcons()"
					:b-alance="formatter.format(insuranceBalance)"
					@click="handleInsuranceClick"
				/>

				<AccountCard
					v-if="creditInfo && creditInfo.length > 0 && showCreditInfo"
					key="credit"
					label="Loans"
					:icon="getCreditInfoIcon()"
					:accountIcons="getAllCreditInfoIcons()"
					:balance="formatter.format(loanBalance)"
					@click="handleCreditClick"
				/>

				<!-- pension -->
				<AccountCard
					v-if="pensionAccounts && pensionAccounts.length > 0 && showPensionCard"
					key="pension"
					label="Pension"
					:icon="require('@/assets/accountTypes/pension.png')"
					:accountIcons="getAllPensionAccountIcons()"
					:balance="formatter.format(pensionBalance)"
					@click="handlePensionClick"
				/>

				<!-- investment -->
				<AccountCard
					v-if="investmentAccounts && investmentAccounts.length > 0 && showInvestmentCard"
					key="investment"
					label="Investments"
					:balance="formatter.format(investmentBalance)"
					:icon="require('@/assets/accountTypes/investments.png')"
					:accountIcons="getAllInvestmentAccountIcons()"
					@click="handleInvestmentClick"
				/>

			</div>

			<!-- Sectioned cards with headers when 5+ cards -->
			<div v-else>
				<!-- Day-to-day Section -->
				<template v-if="(currentAccounts.length > 0 && showCurrentCard) || (creditAccounts.length > 0 && showCreditCard)">
					<SectionHeader
						title="Day-to-day"
						class="section-header"
						@action="navigateToDayToDay"
					/>
					<div class="account-cards-grid">
						<AccountCard
							v-if="currentAccounts.length > 0 && showCurrentCard"
							key="current-account"
							label="Current accounts"
							:icon="getCurrentAccountIcon()"
							:accountIcons="getAllCurrentAccountIcons()"
							:balance="formatter.format(currentAccountsBalance)"
							@click="handleCurrentAccountClick"
						/>

						<AccountCard
							v-if="creditAccounts.length > 0 && showCreditCard"
							key="credit-account"
							label="Credit cards"
							:icon="getCreditCardIcon()"
							:accountIcons="getAllCreditCardIcons()"
							:balance="formatter.format(creditAccountsBalance)"
							@click="handleCreditAccountClick"
						/>

						<AccountCard
							v-if="savingAccounts.length > 0 && showSavingCard"
							key="savings-account"
							label="Savings"
							:icon="getSavingsAccountIcon()"
							:accountIcons="getAllSavingsAccountIcons()"
							:balance="formatter.format(savingAccountsBalance)"
							@click="handleSavingAccountClick"
						/>					
					</div>
				</template>

				<!-- Long-term Section -->
				<template v-if="mortgageInfo && mortgageInfo.length > 0 && showMortgageCard">
					<SectionHeader
						title="Long-term"
						class="section-header"
						@action="navigateToLongTerm"
					/>
					<div class="account-cards-grid">
						<AccountCard
							key="mortgage"
							label="Mortgages"
							:icon="getMortgageIcon()"
							:accountIcons="getAllMortgageAccountIcons()"
							:balance="formatter.format(mortgageBalance)"
							@click="handleMortgageClick"
						/>

						<AccountCard
							v-if="creditInfo && creditInfo.length > 0 && showCreditInfo"
							key="credit"
							label="Loans"
							:icon="getCreditInfoIcon()"
							:accountIcons="getAllCreditInfoIcons()"
							:balance="formatter.format(loanBalance)"
							@click="handleCreditClick"
						/>

						<!-- pension -->
						<AccountCard
							v-if="pensionAccounts && pensionAccounts.length > 0 && showPensionCard"
							key="pension"
							label="Pensions"
							:icon="require('@/assets/accountTypes/pension.png')"
							:accountIcons="getAllPensionAccountIcons()"
							:balance="formatter.format(pensionBalance)"
							@click="handlePensionClick"
						/>

						<!-- investment -->
						<AccountCard
							v-if="investmentAccounts && investmentAccounts.length > 0 && showInvestmentCard"
							key="investment"
							label="Investments"
							:icon="require('@/assets/accountTypes/investments.png')"
							:accountIcons="getAllInvestmentAccountIcons()"
							:balance="formatter.format(investmentBalance)"
							@click="handleInvestmentClick"
						/>
					</div>
				</template>

				<!-- Insurance Section -->
				<template v-if="insuranceInfo && insuranceInfo.length > 0 && showInsuranceCard">
					<SectionHeader
						title="Insurance"
						class="section-header"
						@action="navigateToInsurance"
					/>
					<div class="account-cards-grid">
						<AccountCard
							key="insurance"
							label="Insurance Policies"
							:icon="getInsuranceIcon()"
							:accountIcons="getAllInsuranceAccountIcons()"
							:b-alance="formatter.format(insuranceBalance)"
							@click="handleInsuranceClick"
						/>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
import AccountCard from './AccountCard.vue';
import AccountListItem from './AccountListItem.vue';
import MGButton from './MGButton.vue';
import SectionHeader from './SectionHeader.vue';
import Helpers from '../../mixins/Helpers';


export default {
	name: 'AccountCardsSection',
	mixins: [Helpers],
	emits: ['add-account-click', 'filter-changed', 'suggested-asset-click'],
	components: {
		AccountCard,
		AccountListItem,
		SectionHeader,
		MGButton
	},
	props: {
		currentAccounts: {
			type: Array,
			default: () => []
		},
		currentAccountsBalance: {
			type: Number,
			default: 0
		},
		creditAccounts: {
			type: Array,
			default: () => []
		},
		creditAccountsBalance: {
			type: Number,
			default: 0
		},
		savingAccounts : {
			type: Array,
			default: () => []
		},
		savingAccountsBalance: {
			type: Number,
			default: 0
		},
		mortgageInfo: {
			type: Array,
			default: () => []
		},
		insuranceInfo: {
			type: Array,
			default: () => []
		},
		creditInfo: {
			type: Array,
			default: () => []
		},
		investmentInfo: {
			type: Array,
			default: () => []
		},
			showCurrentCard: {
			type: Boolean,
			default: false
		},
		showCreditCard: {
			type: Boolean,
			default: false
		},
		showSavingCard: {
			type: Boolean,
			default: false
		},
		showMortgageCard: {
			type: Boolean,
			default: false
		},
		showPensionCard: {
			type: Boolean,
			default: false
		},
		showInvestmentCard: {
			type: Boolean,
			default: false
		},
		showInsuranceCard: {
			type: Boolean,
			default: false
		},
		showCreditInfo: {
			type: Boolean,
			default: false
		},
		formatter: {
			type: Object,
			required: true
		},
		selectedSection: {
			type: String,
			default: 'all'
		},
		selectedSubSection: {
			type: String,
			default: 'all'
		},
		suggestedAssets: {
			type: Array,
			default: () => []
		}
	},
	computed: {
		totalVisibleCards() {
			let count = 0;
			if (this.currentAccounts.length > 0 && this.showCurrentCard) count++;
			if (this.creditAccounts.length > 0 && this.showCreditCard) count++;
			if (this.insuranceInfo && this.insuranceInfo.length > 0 && this.showInsuranceCard) count++;
			if (this.mortgageInfo && this.mortgageInfo.length > 0 && this.showMortgageCard) count++;
			if (this.savingAccounts.length > 0 && this.showSavingCard) count++;
			if (this.creditInfo && this.creditInfo.length > 0 && this.showCreditInfo) count++;
			if (this.pensionAccounts && this.pensionAccounts.length > 0 && this.showPensionCard) count++;
			return count;
		},
		showSectionHeaders() {
			return this.totalVisibleCards >= 5;
		},
		isFilterActive() {
			return this.selectedSection !== 'all';
		},
		pensionAccounts() {
			if (!this.investmentInfo) return [];
			return this.investmentInfo.filter(account => account.accountType?.name === 'Pension');
		},
		investmentAccounts() {
			if (!this.investmentInfo) return [];
			return this.investmentInfo.filter(account => account.accountType?.name === 'Investment');
		},
		mortgageBalance() {
			if (!this.mortgageInfo || this.mortgageInfo.length === 0) return 0;
			return this.mortgageInfo.reduce((sum, mortgage) => {
				return sum + (mortgage.account?.balance?.amount || 0);
			}, 0);
		},
		loanBalance() {
			if (!this.creditInfo || this.creditInfo.length === 0) return 0;
			return this.creditInfo.reduce((sum, loan) => {
				return sum + (loan.balance?.amount || 0);
			}, 0);
		},
		pensionBalance() {
			if (!this.pensionAccounts || this.pensionAccounts.length === 0) return 0;
			return this.pensionAccounts.reduce((sum, account) => {
				return sum + (account.balance || 0);
			}, 0);
		},
		investmentBalance() {
			if (!this.investmentAccounts || this.investmentAccounts.length === 0) return 0;
			return this.investmentAccounts.reduce((sum, account) => {
				return sum + (account.balance || 0);
			}, 0);
		},
		insuranceBalance() {
			if (!this.insuranceInfo || this.insuranceInfo.length === 0) return 0;
			return this.insuranceInfo.reduce((sum, policy) => {
				const itemsSum = (policy.insuredItems || []).reduce((itemSum, item) => {
					return itemSum + (item.valueInsured || 0);
				}, 0);
				return sum + itemsSum;
			}, 0);
		},
		suggestedInsuranceAssets() {
			if (!this.suggestedAssets) return [];
			return this.suggestedAssets.filter(a => {
				const s = a.uiElement?.section || '';
				return s === 'Insurance' || s.startsWith('Insurance >');
			});
		},
		suggestedLongTermAssets() {
			if (!this.suggestedAssets) return [];
			const subMap = {
				'mortgages': 'Mortgages',
				'loans': 'Loans',
				'pensions': 'Pensions',
				'investments': 'Investments'
			};
			return this.suggestedAssets.filter(a => {
				const s = a.uiElement?.section || '';
				if (s !== 'Long-term' && !s.startsWith('Long-term >')) return false;
				if (this.selectedSubSection === 'all') return true;
				const subCategory = s.split('> ')[1]?.trim();
				return subCategory === subMap[this.selectedSubSection];
			});
		},
		suggestedDayToDayAssets() {
			if (!this.suggestedAssets) return [];
			return this.suggestedAssets.filter(a => {
				const s = a.uiElement?.section || '';
				return s === 'Day-to-day' || s.startsWith('Day-to-day >');
			});
		},
		isDayToDayEmpty() {
			return this.currentAccounts.length === 0 &&
				this.creditAccounts.length === 0 &&
				this.savingAccounts.length === 0 &&
				this.suggestedDayToDayAssets.length === 0;
		},
		isLongTermEmpty() {
			return (!this.mortgageInfo || this.mortgageInfo.length === 0) &&
				(!this.creditInfo || this.creditInfo.length === 0) &&
				(!this.pensionAccounts || this.pensionAccounts.length === 0) &&
				(!this.investmentAccounts || this.investmentAccounts.length === 0) &&
				this.suggestedLongTermAssets.length === 0;
		},
		isInsuranceEmpty() {
			return (!this.insuranceInfo || this.insuranceInfo.length === 0) &&
				this.suggestedInsuranceAssets.length === 0;
		},
		isCurrentSubSectionEmpty() {
			if (this.selectedSubSection === 'all') return false;
			const subSectionMap = {
				'current': 'Day-to-day > Current',
				'credit': 'Day-to-day > Credit',
				'savings': 'Day-to-day > Savings',
				'mortgages': 'Long-term > Mortgages',
				'pensions': 'Long-term > Pensions',
				'investments': 'Long-term > Investments',
				'loans': 'Long-term > Loans'
			};
			const hasSuggested = this.hasSuggestedForSection(subSectionMap[this.selectedSubSection]);
			// d2d sub-sections
			if (this.selectedSubSection === 'current') return this.currentAccounts.length === 0 && !hasSuggested;
			if (this.selectedSubSection === 'credit') return this.creditAccounts.length === 0 && !hasSuggested;
			if (this.selectedSubSection === 'savings') return this.savingAccounts.length === 0 && !hasSuggested;
			// lt sub-sections
			if (this.selectedSubSection === 'mortgages') return (!this.mortgageInfo || this.mortgageInfo.length === 0) && !hasSuggested;
			if (this.selectedSubSection === 'pensions') return (!this.pensionAccounts || this.pensionAccounts.length === 0) && !hasSuggested;
			if (this.selectedSubSection === 'investments') return (!this.investmentAccounts || this.investmentAccounts.length === 0) && !hasSuggested;
			if (this.selectedSubSection === 'loans') return (!this.creditInfo || this.creditInfo.length === 0) && !hasSuggested;
			return false;
		},
		showEmptyState() {
			// Show empty state for sub-section filter
			if (this.isCurrentSubSectionEmpty) return true;
			// Show empty state for main section filter
			return (this.selectedSection === 'd2d' && this.isDayToDayEmpty) ||
				(this.selectedSection === 'lt' && this.isLongTermEmpty) ||
				(this.selectedSection === 'ins' && this.isInsuranceEmpty);
		},
		emptyStateHeadline() {
			if (this.selectedSection === 'd2d') return 'Track your everyday spending';
			if (this.selectedSection === 'lt') return 'See your long-term money clearly';
			if (this.selectedSection === 'ins') return 'Keep on top of your cover';
			return '';
		},
		emptyStateSubtext() {
			if (this.selectedSection === 'd2d') return "Work out where your money's going – and where you want it to go instead.";
			if (this.selectedSection === 'lt') return "Get a better view of your future finances – what's growing and where you're headed.";
			if (this.selectedSection === 'ins') return "Store your policies here and we'll remind you before anything's up for renewal.";
			return '';
		},
		emptyStateButtonText() {
			// Sub-section specific text
			if (this.selectedSubSection === 'current') return 'Add current account';
			if (this.selectedSubSection === 'credit') return 'Add credit card';
			if (this.selectedSubSection === 'savings') return 'Add savings account';
			if (this.selectedSubSection === 'mortgages') return 'Add mortgage';
			if (this.selectedSubSection === 'pensions') return 'Add pension';
			if (this.selectedSubSection === 'investments') return 'Add investment';
			if (this.selectedSubSection === 'loans') return 'Add loan';
			// Main section text
			if (this.selectedSection === 'd2d') return 'Add day-to-day account';
			if (this.selectedSection === 'lt') return 'Add long term account';
			if (this.selectedSection === 'ins') return 'Add insurance';
			return '';
		}
	},
	methods: {
		properCase(str) {
			if (!str) return '';
			return str.replace(/\b\w/g, c => c.toUpperCase());
		},
		getBrandLogo(name) {
			if (!name) return require('@/assets/round1.png');
			return this._getBrandLogoUrl(name);
		},
		hasSuggestedForSection(prefix) {
			if (!prefix || !this.suggestedAssets) return false;
			return this.suggestedAssets.some(a => {
				const s = a.uiElement?.section || '';
				return s === prefix || s.startsWith(prefix + ' >');
			});
		},
		getDayToDayBalance() {
			const total = (this.showCurrentCard ? this.currentAccountsBalance : 0) +
				(this.showCreditCard ? this.creditAccountsBalance : 0) +
				(this.showSavingCard ? this.savingAccountsBalance : 0);
			return this.formatter.format(total);
		},
		getCurrentAccountsBalance() {
			return this.formatter.format(this.currentAccountsBalance);
		},
		getCreditCardsBalance() {
			return this.formatter.format(this.creditAccountsBalance);
		},
		getSavingAccountsBalance() {
			return this.formatter.format(this.savingAccountsBalance);
		},
		getCurrentAccountIcon() {
			return require('@/assets/accountTypes/current-account.png');
		},
		getCreditCardIcon() {
			return require('@/assets/accountTypes/credit-card.png');
		},
		getMortgageIcon() {
			return require('@/assets/accountTypes/mortgage.png');
		},
		getInsuranceIcon() {
			return require('@/assets/accountTypes/insurance.png');
		},
		getCreditInfoIcon() {
			return require('@/assets/accountTypes/loans.png');
		},
		getSavingsAccountIcon() {
			return require('@/assets/accountTypes/savings-account.png');
		},
		getAccountIcons(items, config) {
			const icons = [];
			if (items && items.length > 0) {
				items.forEach(item => {
					const providerPath = config.providerPath.split('.');
					let provider = item;
					for (const key of providerPath) {
						provider = provider?.[key];
					}

					if (provider) {
						// Use logoUri if available, then BrandLogos API
						let iconUrl = provider.logoUri;

						if (!iconUrl && provider.name) {
							iconUrl = this._getBrandLogoUrl(provider.name);
						}

						if (!iconUrl) {
							iconUrl = require('@/assets/round1.png');
						}

						if (iconUrl) {
							icons.push(iconUrl);
						}
					}
				});
			}
			return icons;
		},
		getSuggestedIconsForSection(sectionMatch) {
			if (!this.suggestedAssets) return [];
			return this.suggestedAssets
				.filter(a => {
					const s = a.uiElement?.section || '';
					return s === sectionMatch || s.startsWith(sectionMatch + ' >');
				})
				.map(a => this.getBrandLogo(a.provider));
		},
		getAllCreditCardIcons() {
			return [
				...this.getAccountIcons(this.creditAccounts, { providerPath: 'bank', typePath: 'accountType', defaultType: 'credit' }),
				...this.getSuggestedIconsForSection('Day-to-day > Credit')
			];
		},
		getAllPensionAccountIcons() {
			return [
				...this.getAccountIcons(this.pensionAccounts, { providerPath: 'provider', typePath: 'accountType', defaultType: 'pension' }),
				...this.getSuggestedIconsForSection('Long-term > Pensions')
			];
		},
		getAllInvestmentAccountIcons() {
			return [
				...this.getAccountIcons(this.investmentAccounts, { providerPath: 'provider', typePath: 'accountType', defaultType: 'investment' }),
				...this.getSuggestedIconsForSection('Long-term > Investments')
			];
		},
		getAllSavingsAccountIcons() {
			return [
				...this.getAccountIcons(this.savingAccounts, { providerPath: 'bank', typePath: 'accountType', defaultType: 'savings' }),
				...this.getSuggestedIconsForSection('Day-to-day > Savings')
			];
		},
		getAllCurrentAccountIcons() {
			return [
				...this.getAccountIcons(this.currentAccounts, { providerPath: 'bank', typePath: 'accountType', defaultType: 'current' }),
				...this.getSuggestedIconsForSection('Day-to-day > Current')
			];
		},
		getAllMortgageAccountIcons() {
			return [
				...this.getAccountIcons(this.mortgageInfo, { providerPath: 'account.provider', typePath: 'policyType', defaultType: 'mortgage' }),
				...this.getSuggestedIconsForSection('Long-term > Mortgages')
			];
		},
		getAllInsuranceAccountIcons() {
			return [
				...this.getAccountIcons(this.insuranceInfo, { providerPath: 'provider', typePath: 'policyType', defaultType: 'insurance' }),
				...this.getSuggestedIconsForSection('Insurance')
			];
		},
		getAllCreditInfoIcons() {
			return [
				...this.getAccountIcons(this.creditInfo, { providerPath: 'provider', typePath: 'creditType', defaultType: 'loans' }),
				...this.getSuggestedIconsForSection('Long-term > Loans')
			];
		},
		handleCurrentAccountClick() {
			this.$emit('filter-changed', { section: 'd2d', subSection: 'current' });
		},
		handleCreditAccountClick() {
			this.$emit('filter-changed', { section: 'd2d', subSection: 'credit' });
		},
		handleSavingAccountClick() {
			this.$emit('filter-changed', { section: 'd2d', subSection: 'savings' });
		},
		handleMortgageClick() {
			this.$emit('filter-changed', { section: 'lt', subSection: 'mortgages' });
		},
		handleCreditClick() {
			this.$emit('filter-changed', { section: 'lt', subSection: 'loans' });
		},
		handleInsuranceClick() {
			this.$emit('filter-changed', { section: 'ins' });
		},
		handlePensionClick() {
			this.$emit('filter-changed', { section: 'lt', subSection: 'pensions' });
		},
		handleInvestmentClick() {
			this.$emit('filter-changed', { section: 'lt', subSection: 'investments' });
		},
		navigateToDayToDay() {
			this.$router.push('/money');
		},
		navigateToLongTerm() {
			this.$router.push('/money');
		},
		navigateToInsurance() {
			this.$router.push('/money');
		},
		handleSpendingAnalysisClick() {
			this.$router.push('/SpendingAnalysis');
		},
		handleAddAccountClick() {
			// Insurance section only has one type, navigate directly
			if (this.selectedSection === 'ins') {
				this.$router.push('/Policies/Insurance/Add');
				return;
			}
			// Sub-sections open the drawer in specific account mode
			const subSections = ['current', 'credit', 'savings', 'mortgages', 'loans', 'pensions', 'investments'];
			if (subSections.includes(this.selectedSubSection)) {
				this.$emit('add-account-click', this.selectedSubSection);
				return;
			}
			// Pass section type to filter the drawer
			this.$emit('add-account-click', this.selectedSection);
		},
		handleAccountListItemClick(account, type) {
			if (type === 'mortgage') {
				this.$router.push(`/Mortgages/${account.id}`);
			} else if (type === 'policy') {
				this.$router.push(`/Policies/${account.id}`);
			} else if (type === 'pension') {
				this.$router.push(`/Pensions/${account.id}`);
			} else if (type === 'investment') {
				this.$router.push(`/Investments/${account.id}`);
			} else if (type === 'loan') {
				this.$router.push(`/Credit/${account.id}`);
			} else {
				// Default for bank accounts, savings, credit cards
				this.$router.push(`/Account/${account.id}`);
			}
		},
								}
}
</script>

<style scoped>
.account-cards-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 16px;
	margin-bottom: 16px;
}

.account-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.section-header {
	margin-top: -8px;
	margin-bottom: -4px;
}

.section-header :deep(.title) {
	font-size: 13px;
}

.empty-state {
	text-align: center;
	padding: 0px 0px;
}

.empty-state-text {
	font-size: 18px;
	font-weight: bold;
	margin: 0 0 8px 0;
	color: #413655;
;
}

.empty-state-subtext {
	font-size: 14px;
	color: #5A506E;
	margin: 0 0 24px 0;
}

.fade-enter-active {
	transition: opacity 1s ease, max-height 1s ease;
	overflow: hidden;
}

.fade-enter-from {
	opacity: 0;
	max-height: 0;
}

.fade-enter-to {
	opacity: 1;
	max-height: 100px;
}
</style>
